import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';

const CompleteSetup: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [facilityName, setFacilityName] = useState('');
    
    const token = searchParams.get('token');
    const facilityId = searchParams.get('facility');

    useEffect(() => {
        if (!token || !facilityId) {
            alert('Invalid setup link');
            navigate('/member/login');
            return;
        }

        // Fetch facility name for display
        fetchFacilityName();
    }, [token, facilityId, navigate]);

    const fetchFacilityName = async () => {
        try {
            // Try the backend API first, with fallback to just showing "your facility"
            const response = await api.get(`/api/facilities/${facilityId}`);
            setFacilityName(response.data.name || 'your facility');
        } catch (error) {
            console.error('Error fetching facility:', error);
            // Set a default name if the API fails
            setFacilityName('your facility');
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (formData.password !== formData.confirmPassword) {
            alert('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            alert('Password must be at least 6 characters long');
            return;
        }

        setLoading(true);

        try {
            const response = await api.post('/api/members/complete-migration-setup', {
                token,
                password: formData.password,
                facilityId
            });

            const data = response.data;

            alert('Account setup completed! Please sign in.');
            
            // Redirect to login with a flag to show payment setup needed
            navigate('/member/login', { 
                state: { 
                    setupCompleted: true,
                    needsPaymentSetup: data.needsPaymentSetup,
                    email: searchParams.get('email') // If email is in URL
                }
            });

        } catch (error: any) {
            console.error('Setup error:', error);
            const errorMessage = error.response?.data?.detail || error.message || 'Failed to complete setup';
            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (!token || !facilityId) {
        return null;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                        Complete Your Account Setup
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Welcome to {facilityName}! Set your password to access your account.
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                required
                                value={formData.password}
                                onChange={handleInputChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500"
                                placeholder="Enter your password"
                                minLength={6}
                            />
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                Confirm Password
                            </label>
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                required
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500"
                                placeholder="Confirm your password"
                                minLength={6}
                            />
                        </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-blue-800 mb-2">What happens next?</h3>
                        <ul className="text-sm text-blue-700 space-y-1">
                            <li>• Your account will be activated</li>
                            <li>• You'll be able to sign in immediately</li>
                            <li>• Add a payment method to complete your membership</li>
                            <li>• Start booking courts and enjoying the facility!</li>
                        </ul>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <div className="flex items-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Setting up account...
                            </div>
                        ) : (
                            'Complete Setup'
                        )}
                    </button>
                </form>

                <div className="text-center">
                    <p className="text-xs text-gray-500">
                        Need help? Contact {facilityName} support for assistance.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CompleteSetup; 